interface CacheEntry<T = any> {
  value: T;
  expiry: number;
  timestamp: number;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  useMemoryCache?: boolean;
  useLocalStorage?: boolean;
}

class CacheManager {
  private memoryCache = new Map<string, CacheEntry>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes default
  private storagePrefix = 'cache_';

  /**
   * Set a value in cache with optional TTL
   * @param key - Cache key
   * @param value - Value to cache (will be JSON serialized)
   * @param options - Cache options
   */
  set<T>(
    key: string,
    value: T,
    options: CacheOptions = {}
  ): void {
    const {
      ttl = this.defaultTTL,
      useMemoryCache = true,
      useLocalStorage = true
    } = options;

    const now = Date.now();
    const entry: CacheEntry<T> = {
      value,
      expiry: now + ttl,
      timestamp: now
    };

    // Store in memory cache
    if (useMemoryCache) {
      this.memoryCache.set(key, entry);
    }

    // Store in localStorage
    if (useLocalStorage) {
      try {
        localStorage.setItem(
          this.storagePrefix + key,
          JSON.stringify(entry)
        );
      } catch (error) {
        console.warn('Failed to store in localStorage:', error);
      }
    }
  }

  /**
   * Get a value from cache
   * @param key - Cache key
   * @param fallback - Fallback value if not found or expired
   * @returns Cached value or fallback
   */
  get<T>(key: string, fallback: T | null = null): T | null {
    const now = Date.now();

    // Check memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && memoryEntry.expiry > now) {
      return memoryEntry.value as T;
    }

    // Remove expired memory entry
    if (memoryEntry && memoryEntry.expiry <= now) {
      this.memoryCache.delete(key);
    }

    // Check localStorage
    try {
      const storageValue = localStorage.getItem(this.storagePrefix + key);
      if (storageValue) {
        const entry: CacheEntry<T> = JSON.parse(storageValue);
        
        if (entry.expiry > now) {
          // Update memory cache with fresh data
          this.memoryCache.set(key, entry);
          return entry.value;
        } else {
          // Remove expired localStorage entry
          localStorage.removeItem(this.storagePrefix + key);
        }
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return fallback;
  }

  /**
   * Check if a key exists and is not expired
   * @param key - Cache key
   * @returns True if key exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Delete a specific cache entry
   * @param key - Cache key to delete
   */
  delete(key: string): void {
    // Remove from memory cache
    this.memoryCache.delete(key);

    // Remove from localStorage
    try {
      localStorage.removeItem(this.storagePrefix + key);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage entries with our prefix
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Get or set a value with a factory function
   * @param key - Cache key
   * @param factory - Function to generate value if not cached
   * @param options - Cache options
   * @returns Cached or newly generated value
   */
  async getOrSet<T>(
    key: string,
    factory: () => Promise<T> | T,
    options: CacheOptions = {}
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const value = await factory();
    this.set(key, value, options);
    return value;
  }

  /**
   * Cleanup expired entries from both memory and localStorage
   */
  cleanup(): void {
    const now = Date.now();

    // Cleanup memory cache
    for (const [key, entry] of this.memoryCache.entries()) {
      if (entry.expiry <= now) {
        this.memoryCache.delete(key);
      }
    }

    // Cleanup localStorage
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              const entry: CacheEntry = JSON.parse(value);
              if (entry.expiry <= now) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // Remove malformed entries
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to cleanup localStorage:', error);
    }
  }

  /**
   * Get cache statistics
   * @returns Object with cache stats
   */
  getStats(): {
    memoryEntries: number;
    localStorageEntries: number;
    totalSize: number;
  } {
    const memoryEntries = this.memoryCache.size;
    let localStorageEntries = 0;
    let totalSize = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          localStorageEntries++;
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += new Blob([value]).size;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to calculate localStorage stats:', error);
    }

    return {
      memoryEntries,
      localStorageEntries,
      totalSize
    };
  }

  /**
   * Set default TTL for cache entries
   * @param ttl - Time to live in milliseconds
   */
  setDefaultTTL(ttl: number): void {
    this.defaultTTL = ttl;
  }

  /**
   * Set storage prefix for localStorage keys
   * @param prefix - Prefix for localStorage keys
   */
  setStoragePrefix(prefix: string): void {
    this.storagePrefix = prefix;
  }
}

// Create singleton instance
const cache = new CacheManager();

// Auto-cleanup expired entries every 5 minutes
setInterval(() => {
  cache.cleanup();
}, 5 * 60 * 1000);

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cache.cleanup();
  });
}

export default cache;

// Export types for TypeScript users
export type { CacheOptions, CacheEntry };

// Usage examples:
/*
// Basic usage
cache.set('user', { id: 1, name: 'John' });
const user = cache.get('user');

// With TTL (1 hour)
cache.set('session', token, { ttl: 60 * 60 * 1000 });

// Memory-only cache
cache.set('temp', data, { useLocalStorage: false });

// localStorage-only cache
cache.set('persistent', data, { useMemoryCache: false });

// Get or set with factory function
const data = await cache.getOrSet('api-data', async () => {
  const response = await fetch('/api/data');
  return response.json();
}, { ttl: 10 * 60 * 1000 }); // 10 minutes

// Check if exists
if (cache.has('user')) {
  console.log('User is cached');
}

// Delete specific entry
cache.delete('user');

// Clear all cache
cache.clear();

// Get cache statistics
const stats = cache.getStats();
console.log(`Memory entries: ${stats.memoryEntries}`);
console.log(`localStorage entries: ${stats.localStorageEntries}`);
console.log(`Total size: ${stats.totalSize} bytes`);
*/